import { inject, Injectable } from '@angular/core';
import { doc, Firestore, serverTimestamp, setDoc, Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { ClientErrorFlagService } from './client-error-flag.service';
import { hashFileName, redactSensitiveText, redactText, splitFileName, stripUndefined } from './client-error-redact';
import { collectMemory } from './client-memory';
import {
    CLIENT_ERROR_COLLECTION,
    CLIENT_ERROR_EVENTS_COLLECTION,
    type ClientCrashDetails,
    type ClientErrorApp,
    type ClientErrorDevice,
    type ClientErrorEventDoc,
    type ClientErrorEventPayload,
    type ClientErrorKind,
    type ClientErrorNetwork,
    type ClientErrorSource,
    type ClientErrorStage,
    type ClientErrorSummary,
    type ClientErrorVisibility,
    type ClientErrorWho,
    type RuntimeErrorReason,
    type UploadErrorReason,
    type UploadErrorStage,
} from './types/client-error';
import { storageHttpStatus } from './upload-storage-error';

/** Minimal shape of a file, so callers that only hold a storage path can still report an error. */
export type ClientErrorFileLike = {
    name: string;
    size: number;
    type: string;
    lastModified: number;
};

export type LogUploadErrorInput = {
    stage: UploadErrorStage;
    reason: UploadErrorReason;
    file: ClientErrorFileLike;
    /** The thrown value, when there is one. Its message, stack and server response are all redacted. */
    error?: unknown;
    /** Used when the failure is a validation rejection rather than a thrown error. */
    message?: string;
    customerId?: string;
    orderId?: string;
    prefix?: string | number;
    /** Full storage path. Only the `{customerId}/{orderId}` prefix is persisted. */
    storagePath?: string;
    uploadStartedAt?: number;
    bytesTransferred?: number;
    totalBytes?: number;
};

export type LogRuntimeErrorInput = {
    reason: RuntimeErrorReason;
    /** Separates a rejected promise from a thrown error, which fail for different reasons. */
    kind?: Extract<ClientErrorKind, 'runtime' | 'unhandled-rejection'>;
    /** The thrown or rejected value. Its message and stack are redacted. */
    error?: unknown;
    /** Used when there is no thrown value, or when it carries no usable message. */
    message?: string;
    /** Where a `window.onerror` came from. */
    source?: ClientErrorSource;
};

const BUFFER_KEY = 'smileart.clientErrorBuffer.v1';
/** Superseded by `BUFFER_KEY`. Removed on start so an upgraded client does not leak an orphan. */
const LEGACY_BUFFER_KEY = 'smileart.uploadErrorBuffer.v1';
const MAX_BUFFERED_EVENTS = 50;
const MAX_EVENTS_PER_SESSION = 30;
const MAX_CONCURRENT_CAPTURES = 10;
const MAX_WRITE_ATTEMPTS = 8;
const DEDUPE_WINDOW_MS = 5_000;
const RETENTION_DAYS = 7;
const DAY_MS = 86_400_000;
const FLAG_RETRY_DELAY_MS = 3_000;
const MAX_FLAG_RETRIES = 5;

/**
 * Records client-side failures so they can be diagnosed after the fact.
 *
 * Covers upload failures (`logUploadError`), anything that reaches the global error handlers
 * (`logRuntimeError`) and sessions that died without unloading (`logCrash`). All three share one
 * redact, buffer and flush path, because all three fail for the same reasons and need the same
 * survival properties.
 *
 * Events are written to `localStorage` first and only then flushed to Firestore. That ordering is the
 * point of the design: the failures most worth capturing are caused by an expired token, a dropped
 * connection or the tab dying outright, and all three also break the Firestore write. Buffering means
 * the report survives until the next session, let alone the next reload.
 *
 * Publication is gated at flush time rather than at capture time, which also resolves the race against
 * the asynchronously loaded feature flag for free.
 *
 * This service must never throw and must never call itself. Its own failures go to the console only.
 */
@Injectable({
    providedIn: 'root',
})
export class ClientErrorLogService {
    private readonly firestore = inject(Firestore);
    private readonly authService = inject(AuthService);
    private readonly flagService = inject(ClientErrorFlagService);
    private readonly deviceService = inject(DeviceDetectorService);
    private readonly router = inject(Router);

    /** Correlates every event this session produces, including the crash report it may leave behind. */
    readonly sessionId = createSessionId();

    private readonly visibility = new VisibilityTracker();
    private readonly recentKeys = new Map<string, number>();
    private readonly activities = new Map<string, number>();

    private memoryBuffer: ClientErrorEventPayload[] = [];
    private localStorageUsable = true;
    private sessionEventCount = 0;
    private activeCaptures = 0;
    private flushing = false;
    private summaryWritten = false;
    private flagRetries = 0;
    private capturing = false;

    constructor() {
        this.discardLegacyBuffer();
        void this.flush();

        if (typeof window !== 'undefined') {
            // A queued event caused by a dropped connection should go out as soon as it returns.
            window.addEventListener('online', () => void this.flush());
        }
    }

    /**
     * Records an upload failure. Fire and forget: never throws, never rejects.
     *
     * @param input - What failed, and everything known about the file and transfer at the time.
     */
    logUploadError(input: LogUploadErrorInput): void {
        void this.captureUpload(input).catch((error: unknown) => console.error('[clientErrorLog] capture failed', error));
    }

    /**
     * Records an error that reached one of the global handlers. Fire and forget.
     *
     * @param input - The thrown or rejected value, and where it surfaced.
     */
    logRuntimeError(input: LogRuntimeErrorInput): void {
        if (this.capturing) {
            // A fault inside the capture path would otherwise report itself, forever.
            console.error('[clientErrorLog] dropping error raised while capturing', input.error ?? input.message);
            return;
        }

        void this.captureRuntime(input).catch((error: unknown) => console.error('[clientErrorLog] capture failed', error));
    }

    /**
     * Records a session that ended without a clean unload. Fire and forget.
     *
     * @param crash - State the dead session left behind, reconstructed from its sentinel record.
     * @param who - Identity the dead session was running as. Must match the current user, or the write
     *     is rejected by the rules and the event is eventually dropped.
     */
    logCrash(crash: ClientCrashDetails, who: ClientErrorWho): void {
        void this.captureCrash(crash, who).catch((error: unknown) => console.error('[clientErrorLog] capture failed', error));
    }

    /**
     * Marks long-running work as in flight, so a crash report can say what the session was doing.
     *
     * "The tab died" and "the tab died 40 seconds into an eight-file upload" are different bug reports.
     *
     * @param label - Coarse activity name, for example `upload`. Never anything identifying.
     * @returns A function that clears the mark. Safe to call more than once.
     */
    beginActivity(label: string): () => void {
        this.activities.set(label, (this.activities.get(label) ?? 0) + 1);
        let cleared = false;

        return () => {
            if (cleared) {
                return;
            }
            cleared = true;

            const remaining = (this.activities.get(label) ?? 1) - 1;
            if (remaining > 0) {
                this.activities.set(label, remaining);
            } else {
                this.activities.delete(label);
            }
        };
    }

    /** Activities currently in flight, for the session sentinel to record on its heartbeat. */
    activeActivities(): string[] {
        return [...this.activities.keys()];
    }

    /**
     * Identity of the signed-in user, in the shape events carry.
     *
     * @returns `undefined` while signed out, when there is no document path to write to anyway.
     */
    currentWho(): ClientErrorWho | undefined {
        const user = this.authService.userSignal$();
        if (!user?.uid) {
            return undefined;
        }

        return {
            uid: user.uid,
            email: user.email ?? undefined,
            displayName: user.displayName ?? undefined,
            customerId: this.authService.firestoreIdSignal$(),
            isAdmin: this.authService.adminSignal$(),
        };
    }

    private async captureUpload(input: LogUploadErrorInput): Promise<void> {
        // Bounds runaway recursion without discarding genuine concurrent failures: the create order
        // form renders eight uploaders, so several failing at once is normal.
        if (!this.admitCapture()) {
            return;
        }

        try {
            const who = this.currentWho();
            if (!who) {
                // Without a uid there is no document path to write to, and the rules would reject it.
                console.error('[clientErrorLog] no authenticated user, dropping event', input.reason);
                return;
            }
            who.customerId = input.customerId ?? who.customerId;
            who.orderId = input.orderId;

            const { basename, extension } = splitFileName(input.file.name);
            const nameHash = await hashFileName(who.uid, basename);

            if (this.isDuplicate(`${input.reason}|${nameHash}|${input.orderId ?? ''}`)) {
                return;
            }

            this.enqueue({
                ...this.buildEnvelope('upload', input.stage, input.reason, who),
                message: redactText(input.message ?? errorMessage(input.error) ?? input.reason, basename, nameHash) ?? input.reason,
                stack: redactText(errorStack(input.error), basename, nameHash),
                storageErrorCode: errorCode(input.error),
                storageHttpStatus: storageHttpStatus(input.error),
                storageServerResponse: redactText(errorServerResponse(input.error), basename, nameHash),
                file: {
                    nameHash,
                    extension,
                    sizeBytes: input.file.size,
                    mimeType: input.file.type,
                    lastModified: input.file.lastModified,
                },
                upload: this.buildProgress(input),
                visibility: this.collectVisibility(input.uploadStartedAt),
            });

            await this.flush();
        } finally {
            this.activeCaptures--;
            this.capturing = this.activeCaptures > 0;
        }
    }

    private async captureRuntime(input: LogRuntimeErrorInput): Promise<void> {
        if (!this.admitCapture()) {
            return;
        }

        try {
            const who = this.currentWho();
            if (!who) {
                console.error('[clientErrorLog] no authenticated user, dropping event', input.reason);
                return;
            }

            const message = redactSensitiveText(input.message ?? errorMessage(input.error) ?? input.reason) ?? input.reason;
            const stack = redactSensitiveText(errorStack(input.error));

            // Keyed on the first stack frame rather than the whole trace, so the same fault raised from
            // the same place collapses even when the surrounding frames differ per invocation.
            if (this.isDuplicate(`${input.reason}|${message}|${firstFrame(stack)}`)) {
                return;
            }

            this.enqueue({
                ...this.buildEnvelope(input.kind ?? 'runtime', 'runtime', input.reason, who),
                message,
                stack,
                source: input.source && {
                    url: redactSensitiveText(input.source.url),
                    line: input.source.line,
                    column: input.source.column,
                },
                visibility: this.collectVisibility(undefined),
            });

            await this.flush();
        } finally {
            this.activeCaptures--;
            this.capturing = this.activeCaptures > 0;
        }
    }

    private async captureCrash(crash: ClientCrashDetails, who: ClientErrorWho): Promise<void> {
        if (!this.admitCapture()) {
            return;
        }

        try {
            this.enqueue({
                ...this.buildEnvelope('crash', 'session', 'session-crashed', who),
                message: describeCrash(crash),
                crash,
                app: {
                    ...this.collectApp(),
                    // The route and build of the session that died, not of the one reporting it.
                    route: crash.lastRoute,
                    buildVersion: crash.buildVersion,
                },
                visibility: { everHidden: crash.hiddenAtLastHeartbeat, hiddenMs: 0 },
            });

            await this.flush();
        } finally {
            this.activeCaptures--;
            this.capturing = this.activeCaptures > 0;
        }
    }

    /**
     * Reserves a capture slot, bounding concurrency and total volume per session.
     *
     * @returns Whether the caller may proceed. Callers that get `true` own a slot and must release it.
     */
    private admitCapture(): boolean {
        if (this.activeCaptures >= MAX_CONCURRENT_CAPTURES || this.sessionEventCount >= MAX_EVENTS_PER_SESSION) {
            return false;
        }
        this.activeCaptures++;
        this.capturing = true;
        return true;
    }

    /** Everything an event carries regardless of what failed. */
    private buildEnvelope(
        kind: ClientErrorKind,
        stage: ClientErrorStage,
        reason: ClientErrorEventPayload['reason'],
        who: ClientErrorWho,
    ): ClientErrorEventPayload {
        return {
            schemaVersion: 2,
            kind,
            stage,
            reason,
            message: reason,
            when: {
                clientTime: new Date().toISOString(),
                timezone: resolveTimezone(),
                sessionId: this.sessionId,
            },
            who,
            device: this.collectDevice(),
            network: collectNetwork(),
            visibility: this.collectVisibility(undefined),
            app: this.collectApp(),
        };
    }

    private enqueue(payload: ClientErrorEventPayload): void {
        this.sessionEventCount++;
        this.appendToBuffer(stripUndefined(payload));
    }

    /**
     * Publishes buffered events, oldest first.
     *
     * Stops at the first event that cannot be published yet so ordering is preserved and nothing is lost.
     */
    private async flush(): Promise<void> {
        if (this.flushing) {
            return;
        }
        this.flushing = true;

        try {
            let buffered = this.readBuffer();

            while (buffered.length > 0) {
                const [event, ...remaining] = buffered;
                if (!event) {
                    break;
                }

                const status = this.flagService.status(event.who, event.kind);

                if (status === 'unknown') {
                    // The flag has not loaded yet. Keep the event and come back to it.
                    this.scheduleFlagRetry();
                    break;
                }

                if (status === 'denied') {
                    buffered = remaining;
                    this.writeBuffer(buffered);
                    continue;
                }

                if (!(await this.writeEvent(event))) {
                    // Offline, or the token is not valid yet. Retried on the next capture or `online`.
                    // An event that can never be written — a crash report belonging to a user who does
                    // not come back, say — is given up on rather than blocking the queue behind it.
                    const attempts = (event.attempts ?? 0) + 1;
                    buffered = attempts >= MAX_WRITE_ATTEMPTS ? remaining : [{ ...event, attempts }, ...remaining];
                    this.writeBuffer(buffered);
                    break;
                }

                buffered = remaining;
                this.writeBuffer(buffered);
                await this.writeSummaryOnce(event);
            }
        } catch (error) {
            console.error('[clientErrorLog] flush failed', error);
        } finally {
            this.flushing = false;
        }
    }

    private async writeEvent(event: ClientErrorEventPayload): Promise<boolean> {
        try {
            const path = `${CLIENT_ERROR_COLLECTION}/${event.who.uid}/${CLIENT_ERROR_EVENTS_COLLECTION}/${createEventId(event.when.clientTime)}`;
            const { attempts: _attempts, ...persisted } = event;
            const document: ClientErrorEventDoc = {
                ...persisted,
                serverTime: serverTimestamp(),
                expiresAt: Timestamp.fromMillis(Date.now() + RETENTION_DAYS * DAY_MS),
            };

            await setDoc(doc(this.firestore, path), stripUndefined(document));
            return true;
        } catch (error) {
            console.error('[clientErrorLog] write failed', error);
            return false;
        }
    }

    /**
     * Writes the per-user summary once per session.
     *
     * Without it the Firebase console shows the parent ids greyed out and one user is indistinguishable
     * from another. Written once and without a counter, because eight uploaders failing together would
     * otherwise contend on a single document.
     */
    private async writeSummaryOnce(event: ClientErrorEventPayload): Promise<void> {
        if (this.summaryWritten) {
            return;
        }

        try {
            const summary: ClientErrorSummary = {
                uid: event.who.uid,
                email: event.who.email,
                displayName: event.who.displayName,
                customerId: event.who.customerId,
                lastKind: event.kind,
                lastReason: event.reason,
                lastStage: event.stage,
                lastSessionId: event.when.sessionId,
                lastClientTime: event.when.clientTime,
                lastErrorAt: serverTimestamp(),
            };

            await setDoc(doc(this.firestore, `${CLIENT_ERROR_COLLECTION}/${event.who.uid}`), stripUndefined(summary), { merge: true });
            this.summaryWritten = true;
        } catch (error) {
            console.error('[clientErrorLog] summary write failed', error);
        }
    }

    /** Collapses retry storms, while keeping distinct failures distinguishable. */
    private isDuplicate(key: string): boolean {
        const now = Date.now();
        for (const [seenKey, seenAt] of this.recentKeys) {
            if (now - seenAt > DEDUPE_WINDOW_MS) {
                this.recentKeys.delete(seenKey);
            }
        }

        if (this.recentKeys.has(key)) {
            return true;
        }

        this.recentKeys.set(key, now);
        return false;
    }

    private buildProgress(input: LogUploadErrorInput): ClientErrorEventPayload['upload'] {
        if (!input.storagePath && input.totalBytes === undefined) {
            return undefined;
        }

        const totalBytes = input.totalBytes ?? input.file.size;
        const bytesTransferred = input.bytesTransferred ?? 0;

        return {
            // Everything up to the last separator, so the leaf holding the file name is never stored.
            storagePathPrefix: input.storagePath
                ? input.storagePath.slice(0, input.storagePath.lastIndexOf('/') + 1).replace(/\/$/, '')
                : [input.customerId, input.orderId].filter(Boolean).join('/'),
            prefix: input.prefix === undefined ? undefined : String(input.prefix),
            bytesTransferred,
            totalBytes,
            percentage: totalBytes > 0 ? Math.round((bytesTransferred / totalBytes) * 100) : 0,
            durationMs: input.uploadStartedAt ? Date.now() - input.uploadStartedAt : 0,
        };
    }

    private collectApp(): ClientErrorApp {
        return {
            environment: environment.production ? 'production' : 'development',
            buildVersion: environment.version,
            route: this.router.url,
            serviceWorkerActive: typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller,
        };
    }

    private collectDevice(): ClientErrorDevice {
        const info = this.deviceService.getDeviceInfo();

        return {
            userAgent: info.userAgent,
            browser: info.browser,
            browserVersion: info.browser_version,
            os: info.os,
            osVersion: info.os_version,
            deviceType: info.deviceType,
            language: navigator.language,
            screen: { width: window.screen?.width ?? 0, height: window.screen?.height ?? 0 },
            viewport: { width: window.innerWidth, height: window.innerHeight },
            devicePixelRatio: window.devicePixelRatio,
            deviceMemoryGb: (navigator as NavigatorWithDeviceMemory).deviceMemory,
            hardwareConcurrency: navigator.hardwareConcurrency,
            touchCapable: navigator.maxTouchPoints > 0,
            memory: collectMemory(),
        };
    }

    private collectVisibility(uploadStartedAt: number | undefined): ClientErrorVisibility {
        const since = uploadStartedAt ?? Date.now();
        const hiddenMs = this.visibility.hiddenMsSince(since);
        return { everHidden: hiddenMs > 0, hiddenMs };
    }

    private scheduleFlagRetry(): void {
        if (this.flagRetries >= MAX_FLAG_RETRIES) {
            return;
        }
        this.flagRetries++;
        setTimeout(() => void this.flush(), FLAG_RETRY_DELAY_MS);
    }

    private appendToBuffer(payload: ClientErrorEventPayload): void {
        const buffered = [...this.readBuffer(), payload];
        // Oldest first, so an unbounded failure loop cannot grow local storage without limit.
        this.writeBuffer(buffered.slice(-MAX_BUFFERED_EVENTS));
    }

    private readBuffer(): ClientErrorEventPayload[] {
        if (!this.localStorageUsable) {
            return this.memoryBuffer;
        }

        try {
            const raw = localStorage.getItem(BUFFER_KEY);
            if (!raw) {
                return [];
            }
            const parsed: unknown = JSON.parse(raw);
            return Array.isArray(parsed) ? (parsed as ClientErrorEventPayload[]) : [];
        } catch {
            // Private browsing, disabled storage or corrupt contents. Degrade to memory for this session.
            this.localStorageUsable = false;
            return this.memoryBuffer;
        }
    }

    private writeBuffer(events: ClientErrorEventPayload[]): void {
        this.memoryBuffer = events;

        if (!this.localStorageUsable) {
            return;
        }

        try {
            localStorage.setItem(BUFFER_KEY, JSON.stringify(events));
        } catch {
            this.localStorageUsable = false;
        }
    }

    /** Events buffered by an earlier build carry the v1 schema and cannot be published as they are. */
    private discardLegacyBuffer(): void {
        try {
            localStorage.removeItem(LEGACY_BUFFER_KEY);
        } catch {
            // Storage is unavailable, so there is nothing buffered to discard either.
        }
    }
}

type NavigatorWithDeviceMemory = Navigator & { deviceMemory?: number };
type NetworkInformationLike = { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
type NavigatorWithConnection = Navigator & { connection?: NetworkInformationLike };

/**
 * Tracks how long the tab spent hidden, so a stalled upload on a backgrounded mobile tab is
 * recognisable rather than looking like an unexplained timeout.
 */
class VisibilityTracker {
    private static readonly MAX_INTERVALS = 100;

    private readonly hiddenIntervals: Array<[number, number]> = [];
    private hiddenSince: number | null = null;

    constructor() {
        if (typeof document === 'undefined') {
            return;
        }

        if (document.visibilityState === 'hidden') {
            this.hiddenSince = Date.now();
        }

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.hiddenSince = Date.now();
                return;
            }

            if (this.hiddenSince !== null) {
                this.hiddenIntervals.push([this.hiddenSince, Date.now()]);
                if (this.hiddenIntervals.length > VisibilityTracker.MAX_INTERVALS) {
                    this.hiddenIntervals.shift();
                }
                this.hiddenSince = null;
            }
        });
    }

    /** Milliseconds the tab was hidden between `since` and now. */
    hiddenMsSince(since: number): number {
        const now = Date.now();
        const open: Array<[number, number]> = this.hiddenSince === null ? [] : [[this.hiddenSince, now]];

        return [...this.hiddenIntervals, ...open].reduce((total, [start, end]) => {
            const overlap = Math.min(end, now) - Math.max(start, since);
            return overlap > 0 ? total + overlap : total;
        }, 0);
    }
}

function collectNetwork(): ClientErrorNetwork {
    const connection = (navigator as NavigatorWithConnection).connection;

    return {
        online: navigator.onLine,
        effectiveType: connection?.effectiveType,
        downlinkMbps: connection?.downlink,
        rttMs: connection?.rtt,
        saveData: connection?.saveData,
    };
}

/** Sortable by document id, so browsing a user's folder in the console is chronological. */
function createEventId(clientTime: string): string {
    return `${clientTime.replace(/[:.]/g, '-')}_${Math.random().toString(36).slice(2, 6)}`;
}

function createSessionId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function resolveTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'unknown';
    }
}

/** A crash has no thrown value, so `message` is assembled from what the dead session last reported. */
function describeCrash(crash: ClientCrashDetails): string {
    const parts = [`Session ended without unloading after ${Math.round(crash.lifetimeMs / 1000)}s on ${crash.lastRoute}`];

    if (crash.memory) {
        parts.push(`heap ${crash.memory.usedJsHeapMb}MB of ${crash.memory.jsHeapLimitMb}MB (${Math.round(crash.memory.pressure * 100)}%)`);
    }
    if (crash.activity.length > 0) {
        parts.push(`in flight: ${crash.activity.join(', ')}`);
    }
    if (crash.hiddenAtLastHeartbeat) {
        parts.push('tab was hidden');
    }

    return parts.join('; ');
}

function firstFrame(stack: string | undefined): string {
    return stack?.split('\n')[1]?.trim() ?? '';
}

function errorMessage(error: unknown): string | undefined {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    // A rejected promise can carry anything, and often carries a plain object.
    if (error && typeof error === 'object') {
        const message = (error as { message?: unknown }).message;
        return typeof message === 'string' ? message : safeStringify(error);
    }
    return undefined;
}

function errorStack(error: unknown): string | undefined {
    if (error instanceof Error) {
        return error.stack;
    }
    const stack = error && typeof error === 'object' ? (error as { stack?: unknown }).stack : undefined;
    return typeof stack === 'string' ? stack : undefined;
}

/** Firebase storage errors carry the most diagnostic value of anything on the document. */
function errorCode(error: unknown): string | undefined {
    if (error && typeof error === 'object' && 'code' in error) {
        const code = (error as { code?: unknown }).code;
        return typeof code === 'string' ? code : undefined;
    }
    return undefined;
}

function errorServerResponse(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') {
        return undefined;
    }

    const direct = (error as { serverResponse?: unknown }).serverResponse;
    if (typeof direct === 'string') {
        return direct;
    }

    // The modular SDK nests it instead.
    const nested = (error as { customData?: { serverResponse?: unknown } }).customData?.serverResponse;
    return typeof nested === 'string' ? nested : undefined;
}

function safeStringify(value: unknown): string | undefined {
    try {
        return JSON.stringify(value);
    } catch {
        // Circular, or a getter that throws. Either way there is nothing to say about it.
        return undefined;
    }
}
